import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { ClasseComposant, ClassesComposantsService } from '../../../services/classes-composants.service';

@Component({
  selector: 'app-classes-composants',
  imports: [CommonModule, FormsModule],
  templateUrl: './classes-composants.component.html',
  styleUrl: './classes-composants.component.scss'
})
export class ClassesComposantsComponent implements OnInit {
  protected pageMode: 'gerer' | 'voir' | 'creer' | 'modifier' | 'supprimer' = 'gerer';
  protected classes: ClasseComposant[] = [];
  protected selectedId = '';
  protected nouveauNom = '';
  protected nomEdition = '';
  protected loading = false;
  protected loadingList = false;
  protected deletingClassId: number | null = null;
  protected success = '';
  protected error = '';
  private readonly apiBaseUrl = environment.apiBaseUrl;

  constructor(
    private readonly classesService: ClassesComposantsService,
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.syncPageMode();
    this.router.events.subscribe((event: unknown) => {
      if (event instanceof NavigationEnd) {
        this.syncPageMode();
        this.loadClasses();
      }
    });
    this.loadClasses();
  }

  protected get showCreateSection(): boolean {
    return this.pageMode === 'gerer' || this.pageMode === 'creer';
  }

  protected get showEditSection(): boolean {
    return this.pageMode === 'gerer' || this.pageMode === 'modifier';
  }

  protected get showListSection(): boolean {
    return this.pageMode === 'gerer' || this.pageMode === 'voir' || this.pageMode === 'supprimer';
  }

  protected get showDeleteActionsInTable(): boolean {
    return this.pageMode === 'gerer' || this.pageMode === 'supprimer';
  }

  protected onSelectChange(): void {
    const selected = this.classes.find((c) => c.id === Number(this.selectedId));
    this.nomEdition = selected?.nom ?? '';
  }

  protected createClasse(): void {
    this.clearMessages();

    const nom = this.nouveauNom.trim();
    if (!nom) {
      this.error = 'Le nom de classe est requis.';
      return;
    }

    const duplicateLocal = this.classes.some((c) => c.nom.trim().toLowerCase() === nom.toLowerCase());
    if (duplicateLocal) {
      this.error = 'Une classe avec ce nom existe déjà.';
      return;
    }

    this.loading = true;
    this.classesService.create(nom).subscribe({
      next: (created) => {
        this.loading = false;
        this.success = `Classe créée : ${created.nom}`;
        this.nouveauNom = '';
        this.loadClasses(created.id);
      },
      error: (err: unknown) => {
        this.loading = false;
        this.error = this.extractErrorMessage(err, 'Création impossible.');
      }
    });
  }

  protected renameClasse(): void {
    this.clearMessages();

    const id = Number(this.selectedId);
    if (!id) {
      this.error = 'Sélectionne une classe à modifier.';
      return;
    }

    const nom = this.nomEdition.trim();
    if (!nom) {
      this.error = 'Le nom de classe est requis.';
      return;
    }

    this.loading = true;
    this.classesService.update(id, nom).subscribe({
      next: (updated) => {
        this.loading = false;
        this.success = `Classe modifiée : ${updated.nom}`;
        this.loadClasses(updated.id);
      },
      error: (err: unknown) => {
        this.loading = false;
        this.error = this.extractErrorMessage(err, 'Modification impossible.');
      }
    });
  }

  protected deleteClasse(): void {
    this.clearMessages();

    const id = Number(this.selectedId);
    if (!id) {
      this.error = 'Sélectionne une classe à supprimer.';
      return;
    }

    const selected = this.classes.find((c) => c.id === id);
    const className = selected?.nom ?? `#${id}`;
    this.loading = true;
    this.classesService.getComposantsCount(id).subscribe({
      next: (count) => {
        if (count > 0) {
          this.loading = false;
          this.error = `Suppression impossible (RESTRICT) : cette classe est utilisée par ${count} composant(s).`;
          window.alert(this.error);
          return;
        }

        const confirmed = window.confirm(`Confirmer la suppression de la classe "${className}" ?`);
        if (!confirmed) {
          this.loading = false;
          return;
        }

        this.classesService.delete(id).subscribe({
          next: () => {
            this.loading = false;
            this.success = 'Classe supprimée.';
            this.selectedId = '';
            this.nomEdition = '';
            this.loadClasses();
          },
          error: (err: unknown) => {
            this.loading = false;
            this.error = this.extractErrorMessage(err, 'Suppression impossible.');
          }
        });
      },
      error: (err: unknown) => {
        this.loading = false;
        this.error = this.extractErrorMessage(err, 'Suppression impossible.');
      }
    });
  }

  protected deleteClasseRow(classe: ClasseComposant): void {
    if (!classe?.id) {
      return;
    }

    this.clearMessages();
    this.deletingClassId = classe.id;

    this.classesService.getComposantsCount(classe.id).subscribe({
      next: (count) => {
        if (count > 0) {
          this.deletingClassId = null;
          this.error = `Suppression impossible (RESTRICT) : la classe "${classe.nom}" est utilisée par ${count} composant(s).`;
          window.alert(this.error);
          return;
        }

        const confirmed = window.confirm(`Confirmer la suppression de la classe "${classe.nom}" ?`);
        if (!confirmed) {
          this.deletingClassId = null;
          return;
        }

        this.classesService.delete(classe.id).subscribe({
          next: () => {
            this.deletingClassId = null;
            this.success = `Classe supprimée : ${classe.nom}`;
            if (Number(this.selectedId) === classe.id) {
              this.selectedId = '';
              this.nomEdition = '';
            }
            this.loadClasses();
          },
          error: (err: unknown) => {
            this.deletingClassId = null;
            this.error = this.extractErrorMessage(err, 'Suppression impossible.');
          }
        });
      },
      error: (err: unknown) => {
        this.deletingClassId = null;
        this.error = this.extractErrorMessage(err, 'Suppression impossible.');
      }
    });
  }

  private loadClasses(selectId?: number): void {
    this.loadingList = true;
    this.classesService.list().subscribe({
      next: async (items) => {
        const normalizedItems = Array.isArray(items) ? items : [];
        this.classes = normalizedItems;
        this.loadingList = false;

        // Defensive fallback: if API proxy shape changes and list resolves empty, retry through fetch.
        if (normalizedItems.length === 0) {
          await this.loadClassesWithFetchFallback();
        }

        if (selectId) {
          this.selectedId = String(selectId);
          this.onSelectChange();
        }
        this.cdr.detectChanges();
      },
      error: (err: unknown) => {
        this.loadingList = false;
        this.error = this.extractErrorMessage(err, 'Impossible de charger les classes composants.');
        this.cdr.detectChanges();
      }
    });
  }

  private async loadClassesWithFetchFallback(): Promise<void> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/classes-composants`, { cache: 'no-store' });
      if (!response.ok) {
        return;
      }

      const payload = (await response.json()) as
        | { items?: ClasseComposant[]; data?: ClasseComposant[]; results?: ClasseComposant[] }
        | ClasseComposant[];

      const items = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.items)
          ? payload.items
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.results)
              ? payload.results
              : [];

      this.zone.run(() => {
        this.classes = items;
        this.cdr.detectChanges();
      });
    } catch {
      // Keep current state when fallback fails.
    }
  }

  private clearMessages(): void {
    this.success = '';
    this.error = '';
  }

  private extractErrorMessage(err: unknown, fallback: string): string {
    const httpErr = err as HttpErrorResponse;
    const detail = (httpErr?.error as { detail?: string } | undefined)?.detail;
    if (detail && detail.trim()) {
      return detail;
    }

    if (httpErr?.status === 400) {
      return 'Requête invalide : vérifie que le nom est renseigné et unique.';
    }

    if (httpErr?.status === 409) {
      return 'Suppression impossible (RESTRICT) : cette classe est utilisée par un ou plusieurs composants.';
    }

    if (typeof httpErr?.message === 'string' && httpErr.message.trim()) {
      return httpErr.message;
    }

    return fallback;
  }

  private syncPageMode(): void {
    const url = this.router.url;
    if (url.includes('/classes-composants/voir')) {
      this.pageMode = 'voir';
      return;
    }
    if (url.includes('/classes-composants/creer')) {
      this.pageMode = 'creer';
      return;
    }
    if (url.includes('/classes-composants/modifier')) {
      this.pageMode = 'modifier';
      return;
    }
    if (url.includes('/classes-composants/supprimer')) {
      this.pageMode = 'supprimer';
      return;
    }
    this.pageMode = 'gerer';
  }
}
